import {
  Box,
  Container,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import Button from '~/components/Button';

import { useFormik } from 'formik';
import { navigate } from 'gatsby';
import { HeaderFooter } from '~/components/HeaderFooter';
import { HelmetSeo } from '~/components/HelmetSeo';
import {
  LiveResultBox,
  amountNewParking,
  busyParkingSpots,
  calculate_live_result,
  calculate_result,
  sumPercentageUsageVehicles,
} from '~/components/ImpactTool';
import {
  InputField,
  InputFieldLabel,
  SectionHeadline,
  SubSectionHeadline,
} from '~/components/ImpactTool/InputComponents';
import {
  initialValues,
  initialValuesDevelopment,
  liveResultSkeleton,
  requiredInputFields,
  validationInputSchema,
} from '~/components/ImpactTool/constants';
import { Typography } from '~/components/Typography';
import IconLoading from '~/images/loader.svg';
import { ImpactToolInput } from '~/types/impactTool';
import {
  localStorageHasImpactData,
  readAndParseImpactToolData,
  writeImpactToolData,
  writeResult,
} from '~/utils/storageHandlerToolForm';

const percentageNumberInputProps = {
  max: 100,
  min: 0,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    initialText: {
      padding: '3rem 0',
    },
    questionSectionHeadline: {
      fontSize: '1.5rem',
      letterSpacing: 0,
      fontFamily: 'BundesSans, sans-serif',
      lineHeight: 1.2,
      margin: '1rem 0',
    },
    formTextLabel: {
      fontSize: '1rem',
      fontFamily: 'BundesSans, sans-serif',
    },
    formInputField: {
      width: 300,

      '&::placeholder': {
        fontStyle: 'italic',
      },
    },
    formWrapperWithInfo: {
      width: 300,
      display: 'flex',
      flexDirection: 'row',
    },
    formInputFieldWithInfo: {
      flexGrow: 1,
      marginRight: 10,
      fontWeight: 'bold',
    },
    inputSection: {
      padding: '1rem 0',
      borderBottom: '1px solid #939697',
      '&:last-of-type': {
        borderBottom: 'none',
      },
    },
    inputSectionLast: {
      padding: '1rem 0',
    },
    floatingBox: {
      [theme.breakpoints.up('lg')]: {
        position: 'fixed',
      },
    },
    floatingBoxWrapper: {
      [theme.breakpoints.up('lg')]: {
        order: 1,
      },
      order: 2,
    },
    contentWrapper: {
      [theme.breakpoints.up('lg')]: {
        order: 2,
      },
      order: 1,
    },
    formInputSm: {
      width: 74,
    },
    formInputMd: {
      width: 105,
    },
    formInputLg: {
      width: 305,
    },
    link: {
      color: '#303030',
      borderBottom: '1px solid #63a615',
      textDecoration: 'none',
      fontFamily: 'BundesSerif',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  })
);

const isDevelopment = process.env.NODE_ENV === 'development';

const ImpactTool: React.FC = () => {
  const classes = useStyles();

  const formik = useFormik({
    initialValues,
    validationSchema: validationInputSchema,
    onSubmit: (values: ImpactToolInput, actions) => {
      const submitIsAllowed = true;
      setCalculating(true);

      if (submitIsAllowed) {
        // Save user data
        writeImpactToolData(values);

        // Calculate and save data to localStorage
        const result = calculate_result(values);
        writeResult(result);

        setCalculating(false);
        actions.setSubmitting(false);
        navigate('/ergebnis');
      }
    },
    validateOnChange: false,
  });

  useEffect(function loadDataAfterReturningFromResult() {
    const isReturnedFromResult =
      new URLSearchParams(window.location.search).get('origin') == 'resultpage';

    if (isReturnedFromResult && localStorageHasImpactData()) {
      const defaultValues = readAndParseImpactToolData();
      // defaultValues.own_calculation = defaultValues.own_calculation === 'tool' ? false :defaultValues.own_calculation === 'own' ? true : undefined
      formik.setValues(defaultValues);
      formik.setTouched(
        {
          location_name: true,
          project_description: true,
          number_new_parking: true,
          usage_new_parking: true,
          sum_construction_cost: true,
          own_calculation: true,
          percentage_pupils: true,
          percentage_pedelec: true,
          percentage_car: true,
          percentage_bike: true,
          percentage_trainandwalk: true,
          percentage_parkandride: true,
          distance_bikeandride: true,
          distance_car: true,
          percentage_municipal_car: true,
          distance_bike: true,
          duration_train: true,
          change_duration_distance: true,
        },
        false
      );
    } else if (isDevelopment) {
      formik.setValues(initialValuesDevelopment);
    } else {
      formik.setValues(initialValues);
    }
  }, []);

  useEffect(() => {
    setLiveResult(calculate_live_result(formik.values));
  }, [formik.values]);

  const [calculating, setCalculating] = useState(false);
  const [liveResult, setLiveResult] = useState(liveResultSkeleton());

  return (
    <HeaderFooter>
      <HelmetSeo noindex />
      <Container maxWidth="xl">
        <Grid container>
          <Grid item xs={12} lg={3} className={classes.floatingBoxWrapper}>
            <Box className={classes.floatingBox}>
              <LiveResultBox result={liveResult} />
            </Box>
          </Grid>
          <Grid item xs={12} lg={6} className={classes.contentWrapper}>
            <Typography component="h2" variant="h1">
              Online-Tool zur Wirkungsschätzung von Fahrradabstellanlagen am
              Bahnhof
            </Typography>
            <Box className={classes.initialText}>
              <Typography>
                Mit diesem Wirkungsrechner können Sie mit wenig Aufwand
                unterschiedliche Wirkungen Ihrer am Bahnhof geplanten
                Fahrradabstellanlage abschätzen. Die Ergebnisse können zum
                Beispiel zur Kommunikation im kommunalpolitischen Kontext sowie
                mit Bürger:innen genutzt werden.
                <br />
                Einige Wirkungen, wie z. B. Einsparungen von CO₂- und
                Luftschadstoffemissionen oder gesundheitliche Effekte, werden
                anhand von Eingangsdaten abschätzt, die Sie eingeben müssen.
                Dazu sind bereits in vielen Feldern Orientierungswerte
                eingetragen, die auf Befragungsergebnissen und Datenrecherchen
                beruhen. Diese sollen Ihnen als Anhaltspunkte für Ihre Eingaben
                dienen. Eine Anpassung der Orientierungswerte an Ihre
                spezifischen lokalen Verhältnisse ist jedoch wichtig, um die
                Wirkungen des geplanten Vorhabens möglichst gut abbilden zu
                können. Die Orientierungswerte können unverändert bleiben,
                sofern Ihnen keine Informationen vorliegen, die Ihre Situation
                besser beschreiben. Pflichtfelder sind mit * markiert.
                <br />
                Bitte beachten Sie: Sie müssen jedes verpflichtende Feld
                mindestens einmal anfassen und ggf. ändern, bevor die Berechnung
                möglich ist.
                <br />
                Weitere Wirkungen Ihrer geplanten Fahrradabstellanlage können
                Sie in einem zweiten Schritt subjektiv beurteilen, zum Beispiel
                die Imagewirkung eines Fahrradparkhauses.
              </Typography>
              <Typography style={{ marginTop: '0.5rem' }}>
                Weitere Informationen zur Wirkungsabschätzung finden Sie im{' '}
                <a
                  className={classes.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://radparken.info/wp-content/uploads/2024/07/wirkungsrechner-fahrradparken-doku.pdf"
                  download
                >
                  Hintergrunddokument
                </a>
                . Dieses enthält auch ein Verzeichnis der im Online-Tool
                zitierten Literatur- und Datenquellen.
              </Typography>
            </Box>
            <form onSubmit={formik.handleSubmit}>
              <Box className={classes.inputSection}>
                <Typography
                  component="h3"
                  className={classes.questionSectionHeadline}
                >
                  Geben Sie die Grunddaten zur geplanten Anlage ein
                </Typography>
                <Grid container spacing={2}>
                  <InputFieldLabel formLabel="An welchem Bahnhof und in welcher Kommune soll die Anlage gebaut werden?*" />
                  <InputField
                    error={
                      formik.touched.location_name &&
                      formik.errors.location_name
                    }
                  >
                    <Input
                      placeholder="z. B. Ostbahnhof Berlin"
                      aria-describedby="my-helper-text"
                      className={(classes.formInputField, classes.formInputLg)}
                      fullWidth
                      name="location_name"
                      value={formik.values.location_name}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.location_name &&
                        Boolean(formik.errors.location_name)
                      }
                      required
                      style={{
                        fontWeight: formik.touched.location_name
                          ? 'bold'
                          : 'normal',
                      }}
                      inputProps={{ maxLength: 45 }}
                    />
                  </InputField>
                  <InputFieldLabel formLabel="Beschreibung des Projekts*" />
                  <InputField
                    error={
                      formik.touched.project_description &&
                      formik.errors.project_description
                    }
                    helperText="maximal 400 Zeichen"
                    tooltipText="Die Informationen zum Bahnhof sowie die Projektbeschreibung werden bei der Aufbereitung der Ergebnisse verwendet, um eine vollständige Übersicht über das Projekt zu geben."
                  >
                    <TextField
                      placeholder="z. B. Fahrradparkhaus mit 500 zugangsgesicherten Plätzen am Südeingang des Bahnhofs"
                      multiline
                      minRows={3}
                      className={classes.formInputField}
                      name="project_description"
                      value={formik.values.project_description}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.project_description &&
                        Boolean(formik.errors.project_description)
                      }
                      inputProps={{ maxLength: 400 }}
                      required
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.project_description
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                    ></TextField>
                  </InputField>
                  <InputFieldLabel
                    formLabel="Anzahl geplante Stellplätze*"
                    linkComponent={
                      <a
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://radparken.info/werkzeuge/planungstool/"
                      >
                        Planungstool Radparken
                      </a>
                    }
                  />
                  <InputField
                    helperText={
                      'Das entspricht durchschnittlich ' +
                      busyParkingSpots(
                        formik.values.number_new_parking
                      ).toFixed(0) +
                      ' ausgelasteten Stellplätzen.'
                    }
                    tooltipText="Summe der neu gebauten Stellplätze.
                    Für die weiteren Berechnungen wird von einer durchschnittlich 90-prozentigen Auslastung der Fahrradabstellanlage ausgegangen. Die Bedarfsabschätzung kann zum Beispiel mit dem &ldquo;Planungstool Radparken&rdquo; vorgenommen werden. Die im Wirkungsrechner einzutragenden Daten finden Sie dort im Tabellenblatt D."
                    error={
                      formik.touched.number_new_parking &&
                      formik.errors.number_new_parking
                    }
                  >
                    <TextField
                      type="number"
                      className={
                        (classes.formInputFieldWithInfo, classes.formInputSm)
                      }
                      name="number_new_parking"
                      value={formik.values.number_new_parking}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.number_new_parking &&
                        Boolean(formik.errors.number_new_parking)
                      }
                      required
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.number_new_parking
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                      inputProps={{ min: 0 }}
                    />
                  </InputField>
                  <InputFieldLabel formLabel="Anteil der Neunutzungen*" />
                  <InputField
                    tooltipText="Beim Bau einer neuen Fahrradabstellanlage wird ein Teil der Anlage von Personen genutzt werden, die bereits heute mit dem Fahrrad zum Bahnhof fahren und ihr Fahrrad in einer bestehenden, künftig rückgebauten Fahrradabstellanlage oder wild abstellen. Der übrige Teil der neuen Anlage wird von sogenannten Neunutzungen belegt. Die Abschätzung des Anteils der Neunutzungen erfolgt im Rahmen der Bedarfsschätzung und Dimensionierung der Fahrradabstellanlage, u. a. in Abhängigkeit von der Auslastung der bestehenden Abstellanlagen, dem geplanten Rückbau dieser Anlagen und der Fahrgastprognose für Ihren Bahnhof. Die Anzahl der Neunutzungen wird auf der Basis der Eingaben zum Anteil der Neunutzungen und der Anzahl der ausgelasteten Stellplätze berechnet. Nur der Anteil der Neunutzungen ist relevant für die durch den Bau der Anlage zu erzielenden Wirkungen. Damit bildet dieser Wert die zentrale Grundlage für die Schätzung der quantitativen Wirkungen."
                    helperText={
                      'Das entspricht durchschnittlich ' +
                      amountNewParking(
                        parseInt(formik.values.number_new_parking) * 0.9,
                        formik.values.usage_new_parking
                      ) +
                      ' Neunutzungen pro Tag.'
                    }
                    error={
                      formik.touched.usage_new_parking &&
                      formik.errors.usage_new_parking
                    }
                  >
                    <TextField
                      type="number"
                      className={
                        (classes.formInputFieldWithInfo, classes.formInputSm)
                      }
                      name="usage_new_parking"
                      value={formik.values.usage_new_parking}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.usage_new_parking &&
                        Boolean(formik.errors.usage_new_parking)
                      }
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.usage_new_parking
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                      inputProps={percentageNumberInputProps}
                    />
                    %
                  </InputField>
                  <InputFieldLabel
                    formLabel="Summe der jährlichen Bau- und Betriebskosten"
                    linkComponent={
                      <a
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://radparken.info/werkzeuge/planungstool/"
                      >
                        Planungstool Radparken
                      </a>
                    }
                  />
                  <InputField
                    tooltipText="Falls Sie Informationen zu den Kosten der Anlage darstellen möchten, geben Sie hier bitte die jährlichen Bau- und Betriebskosten der Anlage an, die Sie mit dem &ldquo;Planungstool Radparken&rdquo; ermittelt haben. Bitte geben Sie den Wert abzüglich der Einnahmen und Fördermittel sowie unter Berücksichtigung der Abschreibung an. Dieser Wert bildet die Summe der jährlichen Bau- und Betriebskosten ab, die Ihrer Kommune entstehen. Eine vorliegende Machbarkeitsstudie o. ä. Untersuchungen können für die &ldquo;Eigene Berechnung&rdquo; herangezogen werden. Ggf. müssen Sie diese Kostenschätzung separat erläutern.
                    Die Angabe der Bau- und Betriebskosten der Fahrradabstellanlage findet keinen Eingang in die weiteren Berechnungen. Die Informationen werden lediglich bei der Aufbereitung der Ergebnisse verwendet, um eine vollständige Übersicht über das Projekt zu geben. "
                    unit="€"
                    error={formik.errors.sum_construction_cost}
                  >
                    <TextField
                      type="number"
                      className={
                        (classes.formInputFieldWithInfo, classes.formInputMd)
                      }
                      name="sum_construction_cost"
                      value={formik.values.sum_construction_cost}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.sum_construction_cost &&
                        Boolean(formik.errors.sum_construction_cost)
                      }
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.sum_construction_cost
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                      inputProps={{ min: 0 }}
                    />
                  </InputField>
                  <InputFieldLabel formLabel="" />
                  <InputField>
                    <RadioGroup
                      aria-label="Herkunft der Summe der Baukosten"
                      name="own_calculation"
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value="tool"
                        control={
                          <Radio
                            required={!!formik.values.sum_construction_cost}
                            disabled={!formik.values.sum_construction_cost}
                            checked={formik.values.own_calculation === 'tool'}
                          />
                        }
                        label="Es wurde das Planungstool Radparken verwendet."
                      />
                      <FormControlLabel
                        value="own"
                        control={
                          <Radio
                            required={!!formik.values.sum_construction_cost}
                            disabled={!formik.values.sum_construction_cost}
                            checked={formik.values.own_calculation === 'own'}
                          />
                        }
                        label="Es wurde eine Eigenberechnung vorgenommen."
                      />
                    </RadioGroup>
                  </InputField>
                </Grid>
              </Box>
              <Box className={classes.inputSection}>
                <SectionHeadline
                  headline="Wer werden Ihrer Einschätzung nach die neuen Nutzer:innen der
            Fahrradabstellanlage sein?"
                />
                <Typography style={{ marginBottom: '1rem' }}>
                  Wie hoch wird Ihrer Einschätzung nach der Anteil der folgenden
                  Nutzer:innengruppen sein und wie ist ihr bisheriges
                  Mobilitätsverhalten? Bitte geben Sie bei den folgenden Fragen
                  nach Möglichkeit spezifische Werte für Ihre Kommune an. Die
                  voreingestellten Werte wurden aus verschiedenen
                  Erhebungsergebnissen abgeleitet und können unverändert
                  bleiben, sofern Ihnen keine Informationen vorliegen, die Ihre
                  Situation besser beschreiben.
                </Typography>
                <Grid container spacing={2}>
                  <InputFieldLabel formLabel="Anteil der Schüler:innen an den Nutzer:innen*" />
                  <InputField
                    tooltipText="Gemäß der Erhebung &ldquo;Mobilität in Deutschland 2017&rdquo; werden 14 % der Bike+Ride-Wege von Schüler:innen durchgeführt. Der Wert ist ggf. in Abhängigkeit der spezifischen Situation vor Ort (z. B. Anbindung eines großen Schulzentrums) anzupassen. (Es werden hierbei nur die Schüler:innen betrachtet, keine Student:innen oder sonstigen Auszubildenden.)"
                    error={formik.errors.percentage_pupils}
                  >
                    <TextField
                      type="number"
                      className={
                        (classes.formInputFieldWithInfo, classes.formInputSm)
                      }
                      name="percentage_pupils"
                      value={formik.values.percentage_pupils}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.percentage_pupils &&
                        Boolean(formik.errors.percentage_pupils)
                      }
                      required
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.percentage_pupils
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                      inputProps={percentageNumberInputProps}
                    />
                    %
                  </InputField>
                  <InputFieldLabel formLabel="Anteil der Pedelecfahrer:innen an den Nutzer:innen*" />
                  <InputField
                    tooltipText="Die Pedelec-Nutzung ist regionsspezifisch sehr unterschiedlich. In Regionen mit Höhenunterschieden oder hoher Kaufkraft kann z. B. von einem eher höheren Pedelec-Anteil ausgegangen werden. Laut Fahrradmonitor 2021 besitzen 14 % der in Deutschland lebenden Personen ein Pedelec. Grundsätzlich steigt mit dem Bau einer gesicherten Fahrradabstellanlage die Wahrscheinlichkeit, dass die Nutzer:innen höherwertige Fahrräder - und damit auch Pedelecs - nutzen."
                    error={formik.errors.percentage_pedelec}
                  >
                    <TextField
                      type="number"
                      className={
                        (classes.formInputFieldWithInfo, classes.formInputSm)
                      }
                      name="percentage_pedelec"
                      value={formik.values.percentage_pedelec}
                      disabled={formik.isSubmitting}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.percentage_pedelec &&
                        Boolean(formik.errors.percentage_pedelec)
                      }
                      required
                      InputProps={{
                        style: {
                          fontWeight: formik.touched.percentage_pedelec
                            ? 'bold'
                            : 'normal',
                        },
                      }}
                      inputProps={percentageNumberInputProps}
                    />
                    %
                  </InputField>
                </Grid>
              </Box>

              <Box className={classes.inputSectionLast}>
                <SectionHeadline headline="Machen Sie bitte Angaben zum derzeitigen Mobilitätsverhalten der erwarteten neuen Nutzer:innen" />
                <Typography>
                  Welche Verkehrsmittel nutzen die künftigen, neuen
                  Bike+Ride-Nutzer:innen Ihrer Einschätzung nach heute
                  überwiegend? Das betrifft sowohl Personen, die Bike+Ride
                  bisher gar nicht nutzen als auch Personen, die bereits heute
                  Bike+Ride nutzen, dies aber aufgrund der neuen
                  Fahrradabstellanlage künftig häufiger tun werden. Die als
                  Voreinstellung angegebenen Werte wurden im Rahmen einer nicht
                  repräsentativen Befragung der Deutschen Bahn im Jahr 2021
                  ermittelt.
                  <br />
                  Die Verkehrsmittelanteile können sich in Abhängigkeit von der
                  Nutzung des Fahrrads im Zu- bzw. Nachlauf zum bzw. vom Bahnhof
                  unterscheiden: Zum Beispiel findet eine Park+Ride-Nutzung
                  (fast) ausschließlich im Zulauf zum Bahnhof statt. Sollten Sie
                  hier eine differenziertere Betrachtung anstellen wollen,
                  können Sie den{' '}
                  <a
                    className={classes.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://radparken.info/wp-content/uploads/2024/07/zu-und-nachlaufrechner-radparkeninfo.xlsx"
                    download
                  >
                    Zu- und Nachlaufrechner (Excel)
                  </a>{' '}
                  nutzen.
                </Typography>
                <Box>
                  <SubSectionHeadline>
                    Bitte geben Sie zunächst den Anteil der jeweiligen
                    Verkehrsmittel(-kombinationen) in Prozent an.
                  </SubSectionHeadline>
                  <Grid container spacing={2}>
                    <InputFieldLabel formLabel="Gesamtstrecke Pkw*" />
                    <InputField
                      tooltipText="Bei xy % der Wege wird aktuell die gesamte Wegstrecke vom Ausgangsort bis zum Ziel mit dem Pkw zurückgelegt."
                      error={formik.errors.percentage_car}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="percentage_car"
                        value={formik.values.percentage_car}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.percentage_car &&
                          Boolean(formik.errors.percentage_car)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.percentage_car
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={percentageNumberInputProps}
                      />
                      %
                    </InputField>
                    <InputFieldLabel formLabel="Gesamtstrecke Fahrrad*" />
                    <InputField
                      tooltipText="Ggf. wird die Fahrradabstellanlage am Bahnhof auch für Wege genutzt, die bisher ganz mit dem Fahrrad zurückgelegt werden, z. B. weil die Nutzer:innen ihr (hochwertiges) Fahrrad nicht ungesichert am Bahnhof abstellen möchten. Bei xy % der Wege wird aktuell die gesamte Wegstrecke vom Ausgangsort bis zum Ziel mit dem Fahrrad zurückgelegt."
                      error={formik.errors.percentage_bike}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="percentage_bike"
                        value={formik.values.percentage_bike}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.percentage_bike &&
                          Boolean(formik.errors.percentage_bike)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.percentage_bike
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={percentageNumberInputProps}
                      />
                      %
                    </InputField>
                    <InputFieldLabel formLabel="Pkw und Bahn (Park+Ride)*" />
                    <InputField
                      tooltipText="Bei xy % der Wege wird aktuell der Teilweg vom Ausgangsort zum Bahnhof mit dem Pkw zurückgelegt."
                      error={formik.errors.percentage_parkandride}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="percentage_parkandride"
                        value={formik.values.percentage_parkandride}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.percentage_parkandride &&
                          Boolean(formik.errors.percentage_parkandride)
                        }
                        inputProps={percentageNumberInputProps}
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.percentage_parkandride
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                      />
                      %
                    </InputField>
                    <InputFieldLabel formLabel="ÖPNV und/oder zu Fuß und Bahn*" />
                    <InputField
                      tooltipText="Bei xy % der Wege wird aktuell der Teilweg vom Ausgangsort zum Bahnhof bzw. vom Bahnhof zum Zielort zu Fuß oder mit dem ÖPNV zurückgelegt."
                      error={formik.errors.percentage_trainandwalk}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="percentage_trainandwalk"
                        value={formik.values.percentage_trainandwalk}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.percentage_trainandwalk &&
                          Boolean(formik.errors.percentage_trainandwalk)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.percentage_trainandwalk
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={percentageNumberInputProps}
                      />
                      %
                    </InputField>
                    <Grid
                      item
                      xs={12}
                      sm={5}
                      style={{ color: '#939697', maxWidth: 300 }}
                    >
                      Summe Verkehrsmittel
                    </Grid>

                    <Grid item xs={12} sm={7} style={{ color: '#939697' }}>
                      <span>
                        {sumPercentageUsageVehicles(
                          formik.values.percentage_car,
                          formik.values.percentage_bike,
                          formik.values.percentage_parkandride,
                          formik.values.percentage_trainandwalk
                        )}
                        %
                      </span>
                      {sumPercentageUsageVehicles(
                        formik.values.percentage_car,
                        formik.values.percentage_bike,
                        formik.values.percentage_parkandride,
                        formik.values.percentage_trainandwalk
                      ) !== 100 && (
                        <FormHelperText error>
                          Die Summe muss 100% ergeben
                        </FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Box>
                  <SubSectionHeadline>
                    Machen Sie bitte Angaben zu den derzeitigen Wegelängen
                  </SubSectionHeadline>
                  <Grid container spacing={2}>
                    <InputFieldLabel formLabel="Durchschnittliche Fahrrad-Streckenlänge (einfacher Weg) der Nutzer:innen von Bike+Ride (Fahrrad und Bahn kombiniert)*" />
                    <InputField
                      unit="km"
                      tooltipText="Welche Strecke legen die Bike+Ride-Nutzer:innen im Zu- oder Nachlauf zum Bahnhof durchschnittlich zurück?
Aus einer Befragung der Deutschen Bahn (N = 5.301) in den Jahren 2021 und 2022 wurde ein Durchschnittswert von 3,5 km (einfache Strecke) von Bike+Ride-Nutzer:innen für den Weg zum Bahnhof ermittelt.
Dabei gaben die Befragten die Wegedauer an. Die Strecke wurde auf Basis einer Durchschnittsgeschwindigkeit von 18 km/h berechnet.
Der Wert muss ggf. in Abhängigkeit von den siedlungsstrukturellen Rahmenbedingungen in der Kommune angepasst werden."
                      error={formik.errors.distance_bikeandride}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="distance_bikeandride"
                        value={formik.values.distance_bikeandride}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.distance_bikeandride &&
                          Boolean(formik.errors.distance_bikeandride)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.distance_bikeandride
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </InputField>

                    <InputFieldLabel formLabel="Durchschnittliche Streckenlänge bisheriger Pkw-Nutzer:innen (Gesamtstrecke Pkw, einfacher Weg)*" />
                    <InputField
                      tooltipText="Welche Wegelängen werden im Durchschnitt von den Pkw-Nutzer:innen zurückgelegt?
                      Berufspendler:innen (die Hauptnutzer:innengruppe von Bike+Ride) legen gemäß Mobilität in Deutschland 2017 durchschnittlich
                      * 32 km zurück, wenn sie Bike+Ride nutzen
                      * 46 km zurück, wenn sie Park+Ride nutzen
                      * 28 km zurück, wenn sie die gesamte Strecke mit dem Pkw zurücklegen
                      (jeweils einfacher Weg).
                      Je nach Siedlungsstruktur sind auch durchschnittliche Wegelängen von 50 km und mehr möglich.
                      Der Wert muss ggf. in Abhängigkeit von den siedlungsstrukturellen Rahmenbedingungen in der Kommune angepasst werden."
                      error={formik.errors.distance_car}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="distance_car"
                        value={formik.values.distance_car}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.distance_car &&
                          Boolean(formik.errors.distance_car)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.distance_car
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                      km
                    </InputField>

                    <InputFieldLabel formLabel="Anteil dieser Pkw-Strecken, der auf Ihrem Gemeindegebiet zurückgelegt wird" />
                    <InputField
                      tooltipText="Um berechnen zu können, welcher Anteil der durch die Fahradabstellanlage zu erzielenden CO₂-Einsparung Ihrer Kommune zugerechnet werden kann, muss hier eine Abschätzung getroffen werden, wie groß der Anteil der Pkw-Strecken im Stadt-/Gemeindegebiet ist."
                      error={formik.errors.percentage_municipal_car}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="percentage_municipal_car"
                        value={formik.values.percentage_municipal_car}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.percentage_municipal_car &&
                          Boolean(formik.errors.percentage_municipal_car)
                        }
                        inputProps={percentageNumberInputProps}
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.percentage_municipal_car
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                      />
                      %
                    </InputField>
                    <InputFieldLabel formLabel="Durchschnittliche Streckenlänge bisheriger Fahrrad-Nutzer:innen (Gesamtstrecke Fahrrad, einfacher Weg)*" />
                    <InputField
                      tooltipText="Ggf. wird die Fahrradabstellanlage am Bahnhof auch für Wege genutzt, die bisher ganz mit dem Fahrrad zurückgelegt werden, z. B. weil die Nutzer:innen ihr (hochwertiges) Fahrrad nicht ungesichert am Bahnhof abstellen möchten. Bei bislang ausschließlich mit dem Fahrrad zurückgelegten Wegen, mit künftiger Nutzung als Bike+Ride-Wege, kann von einer überdurchschnittlichen Länge ausgegangen werden."
                      error={formik.errors.distance_bike}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputSm)
                        }
                        name="distance_bike"
                        value={formik.values.distance_bike}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.distance_bike &&
                          Boolean(formik.errors.distance_bike)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.distance_bike
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                      km
                    </InputField>
                  </Grid>
                </Box>
                <Box>
                  <SubSectionHeadline>
                    Bitte machen Sie Angaben zu den Reisezeiten
                  </SubSectionHeadline>
                  <Grid container spacing={2}>
                    <InputFieldLabel formLabel="Durchschnittliche Reisezeit der Bahnreisenden (einfacher Weg)*" />
                    <InputField
                      tooltipText="Wie lang sind die durchschnittlichen Reisezeiten der Bike+Ride-Nutzer:innen im mit der Bahn zurückgelegten Streckenabschnitt Ihrer Wege?
                          (Bitte geben Sie nur die Fahrzeit im Zug an - ohne Wartezeiten, Zugangszeiten usw.)
                          Dieser Wert muss anhand des Fahrplans und der häufigsten Ziele der Reisenden (z. B. nächstgelegener zentraler Ort, Arbeitsplatzschwerpunkte) abgeschätzt werden."
                      error={formik.errors.duration_train}
                    >
                      <TextField
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputMd)
                        }
                        name="duration_train"
                        value={formik.values.duration_train}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.duration_train &&
                          Boolean(formik.errors.duration_train)
                        }
                        required
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.duration_train
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                        inputProps={{ min: 0 }}
                      />
                      min
                    </InputField>
                    <InputFieldLabel formLabel="Veränderte Umstiegszeit für Bike+Ride-Bestandsnutzer:innen (in Minuten)" />
                    <InputField
                      tooltipText="Erreichen Bike+Ride-Nutzer:innen durch die neue Fahrradabstellanlage die Bahnsteige schneller als heute? Oder brauchen sie länger?
Liegt die neue Fahrradabstellanlage näher an den Bahnsteigen oder weiter weg? Entfallen ggf. Suchzeiten für einen Abstellplatz?
Kommen ggf. Zeiten für die Einfahrt in das Fahrradparkhaus / das Bedienen des Zugangssystems hinzu?
Bitte schätzen Sie ein, wie sich die Zugangszeit zu den Bahnsteigen im Durchschnitt pro Bike+Ride-Nutzer:in verändert.
(Geben Sie den Wert in Minuten an. Negative Werte stehen für einen schnelleren Zugang, also eine Zeitersparnis.)"
                      error={formik.errors.change_duration_distance}
                    >
                      <TextField
                        placeholder="z. B. -1 Minute"
                        type="number"
                        className={
                          (classes.formInputFieldWithInfo, classes.formInputMd)
                        }
                        name="change_duration_distance"
                        value={formik.values.change_duration_distance}
                        disabled={formik.isSubmitting}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.change_duration_distance &&
                          Boolean(formik.errors.change_duration_distance)
                        }
                        InputProps={{
                          style: {
                            fontWeight: formik.touched.change_duration_distance
                              ? 'bold'
                              : 'normal',
                          },
                        }}
                      />
                      min
                    </InputField>
                  </Grid>
                </Box>
              </Box>
              {formik.touched &&
                !requiredInputFields.every((v) => {
                  return Object.keys(formik.touched).includes(v);
                }) && (
                  <FormHelperText error>
                    Sie müssen jedes verpflichtende Feld mindestens einmal
                    anfassen und ggf. ändern, bevor die Berechnung möglich ist.
                    Entsprechende Werte werden dann fett dargestellt.
                  </FormHelperText>
                )}
              {!formik.isValid && (
                <FormHelperText error>
                  Es wurden nicht alle Felder korrekt ausgefüllt. Bitte ändern
                  Sie diese entsprechend.
                </FormHelperText>
              )}
              {sumPercentageUsageVehicles(
                formik.values.percentage_car,
                formik.values.percentage_bike,
                formik.values.percentage_parkandride,
                formik.values.percentage_trainandwalk
              ) !== 100 && (
                <FormHelperText error>
                  Bitte passen Sie die Verkehrsmittel Anteile an.
                </FormHelperText>
              )}
              {formik.isValid && <FormHelperText>&nbsp;</FormHelperText>}

              <Box mt={4} mb={10}>
                <Button
                  type="submit"
                  disabled={
                    !formik.isValid ||
                    !requiredInputFields.every((v) => {
                      return Object.keys(formik.touched).includes(v);
                    })
                  }
                >
                  Ergebnis berechnen
                  {calculating && (
                    <Box style={{ width: 30, height: 30 }}>
                      <IconLoading />
                    </Box>
                  )}
                </Button>
              </Box>
            </form>
          </Grid>
          <Grid item lg={3} style={{ order: 5 }}></Grid>
        </Grid>
      </Container>
    </HeaderFooter>
  );
};

export default ImpactTool;
